"use client";

import { Compass, Pencil, Leaf, Hammer, Sprout } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  number: "01",
  title: "Market Data Analysis",
  description: "Every revenue strategy begins with deep data. We analyze your property's historical performance, local demand patterns, and competitor pricing — collecting the critical insights that will shape your future revenue growth.",
  tags: ["Performance Audit", "Market Benchmarking", "Demand Forecasting", "Client Goals"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_data_analysis.png",
  alt: "Revenue manager analyzing hotel performance data on a digital dashboard",
  icon: Compass
}, {
  number: "02",
  title: "Strategic OTA Planning",
  description: "We translate data into actionable OTA strategies. We compose channel distribution, rate parity, and promotional calendars — testing configurations until the strategy feels inevitable, like it has always been your path to success.",
  tags: ["Channel Strategy", "Rate Parity", "Promotion Planning", "OTA Positioning"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/ota_strategy_planning.webp",
  alt: "Strategic planning session for hotel OTA channel management",
  icon: Pencil
}, {
  number: "03",
  title: "Tech Stack Curation",
  description: "We curate hospitality tech with the eye of an expert — recommending PMS, Channel Managers, and Booking Engines that will integrate gracefully alongside your existing operations and revenue goals.",
  tags: ["PMS Integration", "Booking Engine", "Tech Stack Audit", "Automation Tools"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/tech_stack_curation.png",
  alt: "Curated selection of hospitality technology and software solutions",
  icon: Leaf
}, {
  number: "04",
  title: "Implementation",
  description: "Our team brings the revenue strategy to life with precision. From OTA listing optimization and rate loading to staff training, every step is executed with care for your brand and your bottom line.",
  tags: ["Listing Optimization", "Rate Loading", "Staff Training", "System Setup"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_implementation.png",
  alt: "Revenue management team implementing new OTA listing strategies",
  icon: Hammer
}, {
  number: "05",
  title: "Continuous Growth",
  description: "A revenue strategy is alive — and we stay with it. Weekly performance reviews, dynamic pricing adjustments, and market trend monitoring ensure your hotel matures into the high-revenue sanctuary it was always meant to become.",
  tags: ["Weekly Reviews", "Dynamic Pricing", "Trend Monitoring", "Revenue Support"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_growth_monitoring.webp",
  alt: "Monitoring hotel revenue growth and performance metrics on a screen",
  icon: Sprout
}];
function ProcessStep({
  step,
  index
}: {
  step: (typeof steps)[number];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const isReversed = index % 2 === 1;
  const Icon = step.icon;
  return <div ref={ref} data-index={index} className={`relative grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
      {}
      <div className="hidden lg:block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10" aria-hidden="true">
        <div className="size-3 rounded-lg bg-amber-700 ring-8 ring-slate-50" />
      </div>

      {}
      <div className={`lg:col-span-5 ${isReversed ? "lg:col-start-8" : "lg:col-start-1"}`}>
        <div className="relative overflow-hidden rounded-lg shadow-lg">
          <img src={step.image} alt={step.alt} loading="lazy" decoding="async" className="w-full h-[420px] object-cover transition-transform duration-700 hover:scale-105" />
          <div className="absolute top-5 left-5 inline-flex items-center gap-2 rounded-lg bg-slate-50/90 px-3 py-1.5 backdrop-blur-sm">
            <Icon className="size-3.5 text-amber-700" />
            <span className="font-mono text-[11px] uppercase tracking-widest text-slate-900">
              Stage {step.number}
            </span>
          </div>
        </div>
      </div>

      {}
      <div className={`lg:col-span-6 relative ${isReversed ? "lg:col-start-1 lg:row-start-1" : "lg:col-start-7"}`}>
        {}
        <div className="pointer-events-none select-none absolute -top-10 -left-2 lg:-left-4 font-mono font-light leading-none" style={{
        fontSize: "120px",
        color: "var(--secondary)",
        opacity: 0.18
      }} aria-hidden="true">
          {step.number}
        </div>

        <div className="relative">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-amber-700 mb-4">
            Step {step.number}
          </p>
          <h3 className="font-default text-3xl md:text-4xl lg:text-5xl font-light text-slate-900 tracking-tight mb-5">
            {step.title.split(" ").map((word, i) => <span key={i}>
                {i === 1 || i === 2 ? <em className="italic">{word}</em> : word}{" "}
              </span>)}
          </h3>
          <p className="text-base md:text-lg text-slate-700 leading-relaxed mb-7 max-w-xl">
            {step.description}
          </p>
          <div className="flex flex-wrap gap-2">
            {step.tags.map((tag, i) => <Badge key={i} data-index={i} variant="outline" className="border-amber-700/40 text-slate-900 bg-transparent rounded-lg px-3 py-1 text-xs font-normal hover:bg-amber-700/10 transition-colors">
                {tag}
              </Badge>)}
          </div>
        </div>
      </div>
    </div>;
}
export default function Strategy() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="277" id="listingoptimizationstrategy" className="relative bg-slate-50 text-slate-900 py-24 md:py-32 overflow-hidden">
      <div className="container mx-auto max-w-7xl px-6 lg:px-8">
        {}
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-20 md:mb-28 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-amber-700 mb-6">
            Our Methodology
          </p>
          <h2 className="font-default text-4xl md:text-5xl lg:text-6xl font-light text-slate-900 tracking-tight leading-[1.1] mb-6">
            From <em className="italic font-normal">data</em> to{" "}
            <em className="italic font-normal">revenue</em>
          </h2>
          <p className="text-base md:text-lg text-slate-700 leading-relaxed max-w-[600px] mx-auto">
            Every HotelRevBaBa project unfolds through five carefully considered
            stages — a professional collaboration between data, technology, and
            hospitality expertise that transforms your property's revenue potential.
          </p>
        </div>

        {}
        <div className="relative">
          {}
          <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px -translate-x-1/2 bg-gradient-to-b from-transparent via-amber-700/40 to-transparent" aria-hidden="true" />

          <div className="space-y-24 md:space-y-32">
            {steps.map((step, i) => <ProcessStep key={i} step={step} index={i} />)}
          </div>
        </div>
      </div>
    </section>;
}